import {ApplicationConfig, provideZonelessChangeDetection} from '@angular/core';
import {provideRouter, withComponentInputBinding} from '@angular/router';
import {appRoutes} from './app.routes';
import {acceptLanguageInterceptor, BASE_PATH, provideSdk} from '@on-the-go-web/sdk';

import {BehaviorSubject} from 'rxjs';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {
  DateFnsAdapter,
  MAT_DATE_FNS_FORMATS,
  provideDateFnsAdapter,
} from '@angular/material-date-fns-adapter';
import {nlBE} from 'date-fns/locale';
import {
  ENABLE_PREVIEW_FEATURES_TOKEN,
  LOGGED_IN_TOKEN,
  LOGO_PATH_TOKEN,
  MATOMO_SITE_ID_TOKEN,
  MATOMO_URL_TOKEN,
} from '@on-the-go-web/library';
import {resolveBrowserLanguage} from './helpers/functions/language.functions';
import {paSiteUrl} from './helpers/functions/site-url.functions';
import {
  ENABLE_SUBSCRIPTIONS,
  FACEBOOK_OAUTH_CLIENT_ID,
  GOOGLE_OAUTH_CLIENT_ID,
  INSTAGRAM_OAUTH_CLIENT_ID,
  PA_SITE_URL,
  STRIPE_PUBLISHABLE_KEY,
} from './tokens';
import {AuthService} from './helpers/services/auth.service';
import {provideHttpClient, withFetch, withInterceptors} from '@angular/common/http';
import {provideTranslateCompiler, provideTranslateService} from '@ngx-translate/core';
import {authErrorInterceptor} from './helpers/interceptors/auth-error.interceptor';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {TranslateMessageFormatCompiler} from 'ngx-translate-messageformat-compiler';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
// import {errorInterceptor} from './helpers/interceptors/error.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideHttpClient(
      withFetch(),
      withInterceptors([authErrorInterceptor, acceptLanguageInterceptor /*errorInterceptor*/]),
    ),
    provideSdk(),
    {provide: LOGO_PATH_TOKEN, useValue: new BehaviorSubject('assets/logo_light.png')},
    {
      provide: LOGGED_IN_TOKEN,
      useFactory: (auth: AuthService) => auth.loggedIn$,
      deps: [AuthService],
    },
    {provide: MAT_DATE_LOCALE, useValue: nlBE},
    {provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE]},
    {
      provide: MAT_DATE_FORMATS,
      useValue: {...MAT_DATE_FNS_FORMATS, parse: {dateInput: 'dd/MM/yyyy'}},
    },
    {
      provide: BASE_PATH,
      useValue: window?.APP_CONFIG?.OTG_API_URL ?? 'https://app.develop.aztrix.me/api',
    },
    {provide: MATOMO_URL_TOKEN, useValue: window?.APP_CONFIG?.MATOMO_URL},
    {provide: MATOMO_SITE_ID_TOKEN, useValue: window?.APP_CONFIG?.MATOMO_SITE_ID},
    {provide: STRIPE_PUBLISHABLE_KEY, useValue: window?.APP_CONFIG?.STRIPE_PUBLISHABLE_KEY ?? ''},
    {
      provide: ENABLE_SUBSCRIPTIONS,
      useValue: window?.APP_CONFIG?.ENABLE_SUBSCRIPTIONS === 'true',
    },
    {
      provide: ENABLE_PREVIEW_FEATURES_TOKEN,
      useValue: window?.APP_CONFIG?.ENABLE_PREVIEW_FEATURES === 'true',
    },
    {provide: GOOGLE_OAUTH_CLIENT_ID, useValue: window?.APP_CONFIG?.GOOGLE_OAUTH_CLIENT_ID ?? ''},
    {provide: FACEBOOK_OAUTH_CLIENT_ID, useValue: window?.APP_CONFIG?.FACEBOOK_OAUTH_CLIENT_ID ?? ''},
    {provide: INSTAGRAM_OAUTH_CLIENT_ID, useValue: window?.APP_CONFIG?.INSTAGRAM_OAUTH_CLIENT_ID ?? ''},
    {provide: PA_SITE_URL, useValue: paSiteUrl()},
    provideDateFnsAdapter(),
    provideTranslateService({
      lang: resolveBrowserLanguage(),
      fallbackLang: 'en',
      compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.arb',
      }),
    }),
  ],
};
